import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Upload, 
  Download, 
  Trash2, 
  FileText, 
  Image, 
  File,
  Eye
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface ClienteDocumento {
  id: string;
  cliente_id: string;
  nombre_archivo: string;
  tipo_documento: string;
  url_archivo: string;
  tamaño_archivo: number;
  created_at: string;
}

interface DocumentosTabProps {
  clienteId: string;
  clienteNombre: string;
}

const TIPOS_DOCUMENTO = [
  { value: 'plano', label: 'Plano', icon: FileText },
  { value: 'foto', label: 'Foto', icon: Image },
  { value: 'contrato', label: 'Contrato', icon: FileText },
  { value: 'otro', label: 'Otro', icon: File },
];

export const DocumentosTab: React.FC<DocumentosTabProps> = ({ clienteId, clienteNombre }) => {
  const [documentos, setDocumentos] = useState<ClienteDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchDocumentos();
  }, [clienteId]);

  const fetchDocumentos = async () => {
    try {
      const { data, error } = await supabase
        .from('cliente_documentos')
        .select('*')
        .eq('cliente_id', clienteId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocumentos(data || []);
    } catch (error) {
      console.error('Error fetching documentos:', error);
      toast({
        title: "Error",
        description: "Error al cargar los documentos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !tipoDocumento) return;

    setUploading(true);
    try {
      const { compressImageForUpload } = await import('@/lib/imageUtils');
      setOptimizing(true);
      const uploadFile = await compressImageForUpload(file);
      setOptimizing(false);
      // Upload file to storage
      const fileExt = uploadFile.name.split('.').pop();
      const fileName = `${clienteId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('cliente-documentos')
        .upload(fileName, uploadFile, { contentType: uploadFile.type });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('cliente-documentos')
        .getPublicUrl(fileName);

      // Save document record
      const { error: insertError } = await supabase
        .from('cliente_documentos')
        .insert({
          cliente_id: clienteId,
          nombre_archivo: file.name,
          tipo_documento: tipoDocumento,
          url_archivo: publicUrl,
          tamaño_archivo: uploadFile.size,
        });

      if (insertError) throw insertError;

      toast({
        title: "Documento subido",
        description: "El documento se ha subido correctamente",
      });

      fetchDocumentos();
      setTipoDocumento('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: "Error al subir el documento",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (documento: ClienteDocumento) => {
    try {
      const response = await fetch(documento.url_archivo);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = documento.nombre_archivo;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Error",
        description: "Error al descargar el documento",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (documento: ClienteDocumento) => {
    try {
      // Delete from storage
      const fileName = documento.url_archivo.split('/').pop();
      if (fileName) {
        await supabase.storage
          .from('cliente-documentos')
          .remove([`${clienteId}/${fileName}`]);
      }

      // Delete record
      const { error } = await supabase
        .from('cliente_documentos')
        .delete()
        .eq('id', documento.id);

      if (error) throw error;

      toast({
        title: "Documento eliminado",
        description: "El documento se ha eliminado correctamente",
      });

      fetchDocumentos();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "Error al eliminar el documento",
        variant: "destructive",
      });
    }
  };

  const getDocumentIcon = (tipo: string) => {
    const tipoInfo = TIPOS_DOCUMENTO.find(t => t.value === tipo);
    const IconComponent = tipoInfo?.icon || File;
    return <IconComponent className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Subir Documento
          </CardTitle>
          <CardDescription>
            Sube planos, fotos, contratos y otros documentos del cliente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Tipo de documento" />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_DOCUMENTO.map((tipo) => (
                  <SelectItem key={tipo.value} value={tipo.value}>
                    <div className="flex items-center gap-2">
                      <tipo.icon className="h-4 w-4" />
                      {tipo.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              disabled={!tipoDocumento || uploading}
              className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:disabled:opacity-50"
              accept="image/*,.pdf,.doc,.docx,.txt"
            />
            
            {(uploading || optimizing) && (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">{optimizing ? 'Optimizando imagen…' : 'Subiendo…'}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Documentos del Cliente</CardTitle>
          <CardDescription>
            {documentos.length === 0 
              ? "No hay documentos registrados para este cliente"
              : `${documentos.length} documento${documentos.length !== 1 ? 's' : ''} registrado${documentos.length !== 1 ? 's' : ''}`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {documentos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <File className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay documentos subidos</p>
              <p className="text-sm">Selecciona un tipo de documento y sube el primer archivo</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documentos.map((documento) => (
                <div key={documento.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getDocumentIcon(documento.tipo_documento)}
                    <div>
                      <p className="font-medium text-sm">{documento.nombre_archivo}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {TIPOS_DOCUMENTO.find(t => t.value === documento.tipo_documento)?.label || documento.tipo_documento}
                        </Badge>
                        <span>{formatFileSize(documento.tamaño_archivo)}</span>
                        <span>{new Date(documento.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(documento)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. El documento "{documento.nombre_archivo}" se eliminará permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(documento)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};